/**
 * České popisky automatizací — JEDINÝ zdroj pravdy pro paletu, uzly, porty i chybové
 * hlášky validace. Je v `globals/`, protože texty potřebuje i sdílená `validateGraph`
 * (aby chyba uměla říct „Uzel Porovnání: chybí Veličina" místo id uzlu).
 *
 * Klient je re-exportuje přes `t.automations.*` v lib/dictionary.ts — konvence
 * „všechny UI stringy skrz dictionary" tím zůstává zachovaná.
 *
 * ZÁMĚRNĚ se tu NEPOUŽÍVÁ slovo „pulz" — pro uživatele je to „Událost" vs „Ano/Ne".
 */

import {
  AUTOMATION_NODE_TYPES,
  type AutomationNodeType,
  type DataType,
  type NodeCategory,
} from "@globals/automations/types";

// ── Datové typy portů ────────────────────────────────────────────────────────

export const DATA_TYPE_LABEL: Record<DataType, string> = {
  number: "Číslo",
  boolean: "Ano/Ne",
  pulse: "Událost",
  string: "Text",
  any: "Cokoliv",
};

export const DATA_TYPE_DESC: Record<DataType, string> = {
  number: "Naměřená nebo spočítaná hodnota, např. teplota 23,4 °C.",
  boolean: 'Stav, který platí, nebo neplatí — a drží se v čase (např. „teplota je nad 45 °C").',
  pulse: "Okamžik, kdy se něco stalo (změna hodnoty, čas, tlačítko). Platí jen ten okamžik.",
  string: "Textová hodnota, např. režim měniče.",
  any: "Sem smí jakýkoli typ.",
};

/** Klíčové pravidlo, které musí být vidět v legendě i v tooltipu obou typů. */
export const TYPE_RULE_HINT =
  "Událost a Ano/Ne jdou propojit oběma směry. Událost přivedená do Ano/Ne platí jen v ten okamžik; " +
  "Ano/Ne přivedené do akce ji spustí ve chvíli, kdy podmínka začne platit.";

export const MULTI_EDGE_HINT =
  "Do vstupu Spustit smí vést víc drátů zároveň — chovají se jako NEBO (stačí, aby přišel jeden).";

// ── Kategorie ────────────────────────────────────────────────────────────────

export const CATEGORY_LABEL: Record<NodeCategory, string> = {
  trigger: "Spouštěče",
  value: "Hodnoty",
  logic: "Logika",
  math: "Matematika",
  time: "Čas",
  flow: "Tok",
  action: "Akce",
};

export const CATEGORY_DESC: Record<NodeCategory, string> = {
  trigger: "Určují, KDY se automatizace vyhodnotí.",
  value: "Zdroje hodnot — čidla, konstanty, stáří dat.",
  logic: "Porovnání a skládání podmínek.",
  math: "Počítání s čísly.",
  time: "Podmínky a zdržení závislé na čase.",
  flow: "Řízení toku událostí.",
  action: "Co se má stát.",
};

// ── Porty ────────────────────────────────────────────────────────────────────

/** Popisky portů podle `HandleSpec.labelKey` z registru. */
export const HANDLE_LABEL: Record<string, string> = {
  "automations.handles.pulse": "Událost",
  "automations.handles.value": "Hodnota",
  "automations.handles.run": "Spustit",
  "automations.handles.cond": "Podmínka",
  "automations.handles.rising": "Začalo platit",
  "automations.handles.falling": "Přestalo platit",
  "automations.handles.a": "A",
  "automations.handles.b": "B",
  "automations.handles.in": "Vstup",
  "automations.handles.named": "Hodnota",
};

// ── Uzly: název + „co dělá / kdy použít / příklad" ───────────────────────────

type NodeText = { label: string; desc: string };

export const NODE_TEXT: Record<AutomationNodeType, NodeText> = {
  // A) Spouštěče
  "trigger.topicChange": {
    label: "Změna hodnoty",
    desc: 'Spustí se, když čidlo pošle novou hodnotu. Použij, když reaguješ na událost. Příklad: požární čidlo se přepne z 0 na 1 → režim „Náběžná".',
  },
  "trigger.time": {
    label: "Denní čas",
    desc: "Spustí se každý den v zadanou hodinu ve vybrané dny. Příklad: ve 20:00 poslat souhrn.",
  },
  "trigger.interval": {
    label: "Interval",
    desc: "Spustí se opakovaně po zadaném počtu sekund. Použij pro pravidelnou kontrolu.",
  },
  "trigger.manual": {
    label: "Tlačítko / test",
    desc: "Spustí se jen ručně tlačítkem v editoru. Nejrychlejší způsob, jak ověřit, že notifikace opravdu dorazí.",
  },

  // B) Hodnoty
  "value.topic": {
    label: "Hodnota čidla",
    desc: "Aktuální hodnota vybrané veličiny. Základní stavební kámen podmínek. Příklad: teplota v kuchyni.",
  },
  "value.lastDb": {
    label: "Poslední uložená",
    desc: "Poslední hodnota uložená v databázi (i když čidlo právě mlčí). Použij, když aktuální hodnota nemusí být k dispozici.",
  },
  "value.const": {
    label: "Konstanta",
    desc: "Pevná hodnota (číslo, ano/ne nebo text). Použij, když chceš porovnávat proti pevné mezi zapojené drátem.",
  },
  "value.age": {
    label: "Stáří hodnoty",
    desc: "Kolik sekund uplynulo od poslední zprávy čidla. Použij pro hlídání výpadku. Příklad: stáří > 600 → čidlo je offline.",
  },

  // C) Logika
  "logic.compare": {
    label: "Porovnání",
    desc: "Porovná číslo s mezí a vydá Ano/Ne. Nejčastější uzel vůbec. Příklad: teplota > 45.",
  },
  "logic.and": {
    label: "A zároveň",
    desc: "Platí, jen když platí VŠECHNY zapojené vstupy. Bere podmínky i spouštěče dohromady. Příklad: je noc A zároveň baterie pod 20 %.",
  },
  "logic.or": {
    label: "Nebo",
    desc: "Platí, když platí ASPOŇ JEDEN ze zapojených vstupů. Bere podmínky i spouštěče dohromady. Příklad: požární čidlo se spustilo NEBO teplota je dlouho vysoká.",
  },
  "logic.not": {
    label: "Opak (NE)",
    desc: "Otočí Ano na Ne a naopak. Příklad: čerpadlo NEběží.",
  },
  "logic.hysteresis": {
    label: "Hystereze",
    desc: "Sepne nad horní mezí a vypne až pod dolní — brání kmitání kolem jedné hodnoty. Příklad: pod 20 % baterie přepni na síť, zpět na solár až nad 80 %.",
  },

  // D) Matematika
  "math.op": {
    label: "Matematika",
    desc: "Sečte, odečte, vynásobí nebo vydělí dvě čísla. Příklad: výkon PV1 + výkon PV2.",
  },
  "math.deltaOverTime": {
    label: "Změna za čas",
    desc: "O kolik se hodnota změnila za zadané okno. Příklad: teplota vzrostla o víc než 5 °C za 10 minut.",
  },
  "math.formula": {
    label: "Vzorec",
    desc: "Pro pokročilé: vlastní výraz nad pojmenovanými vstupy a veličinami. Použij, až když to ostatní uzly nezvládnou.",
  },

  // E) Čas / tok
  "time.sustained": {
    label: "Trvá po dobu",
    desc: "Platí, teprve když vstupní podmínka platí nepřetržitě zadanou dobu. Příklad: teplota je nad 45 °C déle než minutu. Přivádí se sem podmínka, ne událost.",
  },
  "time.window": {
    label: "Časové okno",
    desc: "Platí jen mezi zadanými časy ve vybrané dny (funguje i přes půlnoc). Příklad: 22:00–06:00.",
  },
  "time.gate": {
    label: "Brána",
    desc: "Událost projde dál, jen když zároveň platí podmínka. Příklad: pusť notifikaci jen v noci.",
  },
  "flow.risingEdge": {
    label: "Při splnění",
    desc: "Z podmínky udělá dvě události: když začne platit a když přestane. Použij, když chceš reagovat i na návrat do normálu.",
  },
  "flow.throttle": {
    label: "Nejvýše 1× za",
    desc: "Propustí událost nejvýš jednou za zadanou dobu. Ochrana proti záplavě notifikací. Přežije i restart serveru.",
  },
  "time.delay": {
    label: "Zpoždění",
    desc: "Pošle událost dál až po zadané době. Příklad: počkej minutu, jestli se to nespraví samo.",
  },

  // F) Akce
  "action.notify": {
    label: "Notifikace",
    desc: "Pošle notifikaci (Web Push, ntfy, Telegram, Bark). Spustí se, když do vstupu Spustit přijde událost — nebo když podmínka zapojená do Spustit začne platit.",
  },
  "action.log": {
    label: "Zápis do logu",
    desc: "Zapíše řádek do serverového logu. Šikovné na ladění, když nechceš zahltit telefon.",
  },

  // F2) Akce nad zařízeními
  "action.setPipParam": {
    label: "Nastavit PIP",
    desc: "Změní parametr měniče PIP. Jen pro administrátora; zápis prochází bezpečnostní kontrolou a ověří se zpětným čtením.",
  },
  "action.setTracerParam": {
    label: "Nastavit Tracer",
    desc: "Změní parametr regulátoru Tracer. Jen pro administrátora; zápis se ověří zpětným čtením.",
  },
  "action.setBmsParam": {
    label: "Nastavit BMS",
    desc: "Přepne mosfety nebo balancér BMS. Jen pro administrátora; zápis se ověří zpětným čtením.",
  },
  "action.publishMqtt": {
    label: "Publikovat MQTT",
    desc: "Pošle hodnotu do MQTT topicu. Jen pro administrátora. Použij pro ovládání vlastní elektroniky (např. relé na PicoW).",
  },
};

/** Zkratka pro časté použití (paleta, hlavička uzlu). */
export const NODE_LABEL: Record<AutomationNodeType, string> = Object.fromEntries(
  AUTOMATION_NODE_TYPES.map((t) => [t, NODE_TEXT[t].label])
) as Record<AutomationNodeType, string>;

// ── Parametry: popisek pole + nápověda ───────────────────────────────────────

type ParamText = { label: string; hint: string };

export const PARAM_TEXT: Record<AutomationNodeType, Record<string, ParamText>> = {
  "trigger.topicChange": {
    topicId: { label: "Veličina", hint: "Čidlo, jehož změnu sleduješ." },
    mode: {
      label: "Kdy spustit",
      hint: "Náběžná = jen přechod ne→ano (0→1), typicky požární čidlo. Jakákoli změna = při každé nové hodnotě.",
    },
    deltaMin: {
      label: "Min. změna",
      hint: 'Jen pro režim „Změna o ≥": o kolik se hodnota musí lišit, aby to spustilo.',
    },
  },
  "trigger.time": {
    time: { label: "Čas (HH:MM)", hint: "Denní čas ve středoevropském pásmu, včetně letního času." },
    daysOfWeek: { label: "Dny", hint: "Ve které dny se má spustit." },
    missedPolicy: {
      label: "Po výpadku",
      hint: "Co dělat, když byl server v ten čas vypnutý. Dohnat = spustí se do hodiny po startu.",
    },
  },
  "trigger.interval": {
    everySeconds: { label: "Každých (s)", hint: "Minimálně 10 sekund." },
    align: { label: "Zarovnání", hint: "Na minutu/hodinu = spouští se v celou, ne od okamžiku startu serveru." },
  },
  "trigger.manual": {
    label: { label: "Popisek tlačítka", hint: "Text tlačítka, kterým automatizaci spustíš ručně z editoru." },
  },
  "value.topic": { topicId: { label: "Veličina", hint: "Kterou hodnotu má uzel číst." } },
  "value.lastDb": { topicId: { label: "Veličina", hint: "Přečte poslední hodnotu uloženou v databázi." } },
  "value.const": {
    valueType: { label: "Typ", hint: "Určuje i typ výstupního portu." },
    value: { label: "Hodnota", hint: "Pevná hodnota, kterou uzel vydává." },
  },
  "value.age": { topicId: { label: "Veličina", hint: "Vydá počet sekund od poslední zprávy tohoto čidla." } },
  "logic.compare": {
    operator: { label: "Operátor", hint: "Jak se má levá hodnota porovnat s pravou." },
    bMode: { label: "Druhá hodnota", hint: "Konstanta = napíšeš číslo níž. Ze vstupu = přivedeš druhým drátem." },
    bConst: { label: "Konstanta", hint: "Mez, se kterou se porovnává. Příklad: 45." },
    tolerance: {
      label: "Tolerance",
      hint: "Jen pro = a ≠: o kolik se hodnoty smí lišit a pořád se berou jako stejné.",
    },
  },
  "logic.and": {},
  "logic.or": {},
  "logic.not": {},
  "logic.hysteresis": {
    direction: { label: "Směr", hint: "Zapni nahoře = sepne nad horní mezí. Zapni dole = sepne pod dolní mezí." },
    onValue: { label: "Zapni na", hint: "Mez, při které se přepne na Ano." },
    offValue: {
      label: "Vypni na",
      hint: 'Mez, při které se přepne zpět na Ne. Musí být dál než „Zapni na", jinak hystereze nic neřeší.',
    },
    initial: { label: "Výchozí zapnuto", hint: "Stav po prvním spuštění, než dorazí data." },
  },
  "math.op": {
    op: { label: "Operace", hint: 'Dělení nulou vrací „neznámou" — akce pak nevystřelí.' },
    bMode: { label: "Druhá hodnota", hint: "Konstanta = napíšeš číslo níž. Ze vstupu = přivedeš druhým drátem." },
    bConst: { label: "Konstanta", hint: "Druhý operand." },
  },
  "math.deltaOverTime": {
    topicId: { label: "Veličina", hint: "Nad kterou veličinou se změna počítá." },
    windowSeconds: { label: "Okno (s)", hint: "Za jak dlouhou dobu zpět se změna měří." },
    direction: { label: "Směr", hint: "Nárůst / Pokles / Libovolný." },
  },
  "math.formula": {
    expression: {
      label: "Vzorec",
      hint: "Např. a + b nebo {zige/pozar0/temp/val} * 2. Písmena odpovídají pojmenovaným vstupům níž.",
    },
    inputs: { label: "Vstupy (a–h)", hint: "Přidáním písmene vznikne nový vstupní port se stejným názvem." },
    outType: { label: "Výstup", hint: "Jaký typ má vzorec vracet." },
  },
  "time.sustained": {
    seconds: {
      label: "Doba (s)",
      hint: "Jak dlouho musí podmínka nepřetržitě platit. 60 = jedna minuta. Odpočet přežije i restart serveru.",
    },
  },
  "time.window": {
    from: { label: "Od (HH:MM)", hint: "Začátek okna." },
    to: { label: "Do (HH:MM)", hint: "Konec okna. Když je dřív než začátek, okno jde přes půlnoc." },
    daysOfWeek: { label: "Dny", hint: "Ve které dny okno platí." },
  },
  "time.gate": {},
  "flow.risingEdge": {},
  "flow.throttle": {
    seconds: { label: "Nejvýše 1× za (s)", hint: "Další události se v této době zahodí. Přežije restart serveru." },
  },
  "time.delay": {
    seconds: { label: "Zpoždění (s)", hint: "O kolik se událost odloží. Odpočet přežije restart serveru." },
    mode: {
      label: "Při novém pulzu",
      hint: "Restartovat = odpočet začne znovu. Ignorovat = nová událost se během čekání zahodí.",
    },
    missedPolicy: { label: "Po výpadku", hint: "Co dělat, když odpočet dozrál, zatímco byl server vypnutý." },
  },
  "action.notify": {
    severity: {
      label: "Závažnost",
      hint: "Info = tiché. Varování = běžná notifikace. Vážné = hlasité, sbalí se do jednoho vlákna a opakuje se podle cooldownu.",
    },
    title: { label: "Titulek", hint: "První řádek notifikace. Bez něj dorazí zpráva bez nadpisu." },
    body: {
      label: "Text",
      hint: "Můžeš vložit živou hodnotu: {zige/pozar0/temp/val} vezme hodnotu z čidla, {vstup:název} hodnotu z drátu.",
    },
    cooldownSeconds: {
      label: "Cooldown (s)",
      hint: "Nejkratší doba mezi dvěma stejnými notifikacemi. 0 = bez omezení.",
    },
    valueInputs: {
      label: "Hodnoty do textu",
      hint: "Přidáním názvu vznikne vstupní port; v textu na něj odkážeš jako {vstup:název}.",
    },
  },
  "action.log": {
    message: { label: "Zpráva", hint: "Text zapsaný do serverového logu. Podporuje {topic} i {vstup:název}." },
    valueInputs: {
      label: "Hodnoty do textu",
      hint: "Přidáním názvu vznikne vstupní port; v textu na něj odkážeš jako {vstup:název}.",
    },
  },
  "action.setPipParam": { device: { label: "Měnič PIP", hint: "Zařízení, parametr a hodnota, která se zapíše." } },
  "action.setTracerParam": {
    device: { label: "Regulátor Tracer", hint: "Zařízení, parametr a hodnota, která se zapíše." },
  },
  "action.setBmsParam": { device: { label: "BMS", hint: "Zařízení, parametr a hodnota, která se zapíše." } },
  "action.publishMqtt": {
    topic: {
      label: "MQTT topic",
      hint: "Kam se hodnota publikuje. Telemetrické topicy jsou zakázané (hrozila by smyčka).",
    },
    valueMode: { label: "Zdroj hodnoty", hint: "Konstanta = napíšeš níž. Ze vstupu = přivedeš drátem." },
    constValue: { label: "Hodnota (konstanta)", hint: "Co se publikuje." },
    retain: { label: "Retain", hint: "Broker si hodnotu zapamatuje a pošle ji novým odběratelům." },
  },
};

/** Popisek parametru; fallback na klíč, ať nikdy nespadne na prázdný string. */
export const paramLabel = (type: AutomationNodeType, key: string): string => PARAM_TEXT[type]?.[key]?.label ?? key;

export const paramHint = (type: AutomationNodeType, key: string): string | undefined => PARAM_TEXT[type]?.[key]?.hint;

/** Popisek portu z `HandleSpec.labelKey`; fallback na id handlu. */
export const handleLabel = (labelKey: string | undefined, handleId: string): string =>
  (labelKey ? HANDLE_LABEL[labelKey] : undefined) ?? handleId;
